<template>
  <div class="vote-details-list">
    <template v-for="fullTextVote in activeVotes">
      <v-row
        v-if="fullTextVote.blinded"
        :key="fullTextVote.id"
        no-gutters
        class="text-body-2 pb-2 align-center"
        data-testid="vote-details-blinded"
      >
        <researcher-avatar
          :researcher="researcherFor(fullTextVote)"
          small
        />
        <span class="ml-2 grey--text">{{
          `${voterName(
            fullTextVote
          )}'s review is hidden until consensus is reached.`
        }}</span>
      </v-row>
      <v-row
        v-else
        :key="fullTextVote.id"
        no-gutters
        class="text-body-2 pb-2"
        data-testid="vote-details-row"
      >
        <researcher-avatar
          :researcher="researcherFor(fullTextVote)"
          small
        />
        <div class="ml-2">
          <div class="font-weight-medium">
            {{ voterName(fullTextVote) }} voted
            <v-icon
              class="material-icons-outlined mx-1"
              small
              :color="voteProps(fullTextVote).color"
            >
              {{ voteProps(fullTextVote).icon }}
            </v-icon>
            <span class="font-weight-bold">
              {{ voteProps(fullTextVote).text }}
            </span>
          </div>
          <div v-if="fullTextVote.reasonReviewCriteria">
            Criterion:
            {{ fullTextVote.reasonReviewCriteria.description }}
          </div>
          <div v-if="fullTextVote.notes" class="font-weight-light">
            Notes: {{ fullTextVote.notes }}
          </div>
        </div>
      </v-row>
    </template>
    <v-row
      v-if="showOutcomeLine"
      no-gutters
      class="text-body-2 pb-2"
      data-testid="vote-details-outcome"
    >
      <researcher-avatar :researcher="outcomeResearcher" small />
      <div class="ml-2">
        <div class="font-weight-medium">
          {{ outcomeResolverName }} resolved this as
          <v-icon
            class="material-icons-outlined mx-1"
            small
            :color="outcomeProps.color"
          >
            {{ outcomeProps.icon }}
          </v-icon>
          <span class="font-weight-bold">{{
            outcomeProps.text
          }}</span>
        </div>
        <div v-if="outcome.reasonReviewCriteria">
          Criterion: {{ outcome.reasonReviewCriteria.description }}
        </div>
      </div>
    </v-row>
  </div>
</template>

<script>
import ResearcherAvatar from '@/components/Common/ResearcherAvatar';
import { Reference, Researcher } from '@/models';
import { InclusionAssessment } from '@/enums';

const VOTE_TO_PROPS = {
  [InclusionAssessment.INCLUDE]: {
    icon: 'done',
    color: 'success',
    text: 'Include',
  },
  [InclusionAssessment.EXCLUDE]: {
    icon: 'close',
    color: 'error',
    text: 'Exclude',
  },
};

export default {
  name: 'VoteDetailsList',
  components: { ResearcherAvatar },
  props: {
    reference: {
      type: Reference,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      default: null,
    },
  },
  computed: {
    activeVotes() {
      // Guard: the Reference model leaves fullTextReviewVotes undefined when a
      // query didn't select it.
      return (this.reference.fullTextReviewVotes || []).filter(
        (vote) => !vote.archived
      );
    },
    outcome() {
      return this.reference.fullTextReviewOutcome;
    },
    showOutcomeLine() {
      return (
        this.reference.hasFullTextReviewOutcome() &&
        !this.outcome.isConsensus()
      );
    },
    outcomeResearcher() {
      return this.outcome.resolvedBy?.researcher ?? null;
    },
    outcomeResolverName() {
      return this.nameFor(this.outcomeResearcher);
    },
    outcomeProps() {
      return VOTE_TO_PROPS[this.outcome.outcome];
    },
  },
  methods: {
    researcherFor(vote) {
      return vote.reviewer?.researcher ?? null;
    },
    voterName(vote) {
      return this.nameFor(this.researcherFor(vote));
    },
    nameFor(researcher) {
      if (!researcher) return 'A deleted user';
      if (
        this.currentResearcher &&
        researcher.id === this.currentResearcher.id
      ) {
        return 'You';
      }
      return researcher.name;
    },
    voteProps(vote) {
      return VOTE_TO_PROPS[vote.vote];
    },
  },
};
</script>
